import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { emailOtp } from "@/lib/auth-client";

type Step = "email" | "code" | "new-password";
type CodeError = "invalid" | "expired" | null;

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ForgotPasswordModal = ({
  isOpen,
  onClose,
}: ForgotPasswordModalProps) => {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailNotFound, setEmailNotFound] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [codeError, setCodeError] = useState<CodeError>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [verifiedOtp, setVerifiedOtp] = useState("");

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setStep("email");
      setEmail("");
      setCode("");
      setEmailNotFound(false);
      setResendCooldown(0);
      setCodeError(null);
      setNewPassword("");
      setConfirmPassword("");
      setPasswordError(null);
      setVerifiedOtp("");
    }
  }, [isOpen]);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsLoading(true);
    setEmailNotFound(false);

    try {
      // Use BetterAuth emailOtp to send forget-password OTP
      const { error } = await emailOtp.sendVerificationOtp({
        email,
        type: "forget-password",
      });

      if (error) {
        console.error("Forgot password error:", error);
        // Check if it's a Google account error
        if (
          error.code === "GOOGLE_ACCOUNT" ||
          error.message?.includes("google")
        ) {
          toast.error(
            "This account uses Google sign-in. Please use 'Sign in with Google' to access your account.",
          );
          setIsLoading(false);
          return;
        }
        // Check if it's a "not found" error
        else if (
          error.status === 404 ||
          error.code === "EMAIL_NOT_FOUND" ||
          error.code === "USER_NOT_FOUND"
        ) {
          setEmailNotFound(true);
        } else {
          toast.error(
            error.message || "Failed to send reset code. Please try again.",
          );
        }
        setIsLoading(false);
        return;
      }

      // OTP sent successfully, move to code entry step
      toast.success("Reset code sent to your email. Please check your inbox.");
      setStep("code");
      setResendCooldown(30);
    } catch (err: any) {
      console.error("Forgot password error:", err);
      toast.error("Failed to send reset code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;

    setIsLoading(true);
    setCodeError(null);

    try {
      const { error } = await emailOtp.checkVerificationOtp({
        email,
        type: "forget-password",
        otp: code,
      });

      if (error) {
        if (
          error.code === "INVALID_OTP" ||
          error.message?.includes("invalid")
        ) {
          setCodeError("invalid");
        } else if (
          error.code === "OTP_EXPIRED" ||
          error.message?.includes("expired")
        ) {
          setCodeError("expired");
        } else {
          toast.error(error.message || "Failed to verify code.");
        }
        setIsLoading(false);
        return;
      }

      // Store the verified OTP for password reset
      setVerifiedOtp(code);
      setStep("new-password");
    } catch (err) {
      toast.error("Failed to verify code.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password length
    if (newPassword.length < 8) {
      setPasswordError("The password should be at least 8 characters.");
      return;
    }

    // Validate password complexity
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumbers = /\d/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
      setPasswordError(
        "Password must contain at least one uppercase letter, one lowercase letter, and one number.",
      );
      return;
    }

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords don't match.");
      return;
    }

    setIsLoading(true);
    setPasswordError(null);

    try {
      // Use BetterAuth emailOtp to reset password with OTP
      const { error } = await emailOtp.resetPassword({
        email,
        otp: verifiedOtp,
        password: newPassword,
      });

      if (error) {
        console.error("Reset password error:", error);
        // Check for OTP-related errors
        if (
          error.code === "INVALID_OTP" ||
          error.message?.includes("invalid")
        ) {
          setCodeError("invalid");
          setStep("code");
          setCode("");
        } else if (
          error.code === "OTP_EXPIRED" ||
          error.message?.includes("expired")
        ) {
          setCodeError("expired");
          setStep("code");
          setCode("");
        } else {
          toast.error(
            error.message || "Failed to reset password. Please try again.",
          );
        }
        setIsLoading(false);
        return;
      }

      // Password reset successful
      toast.success("Password updated successfully! You can now log in.");
      handleClose();
    } catch (err: any) {
      console.error("Reset password error:", err);
      toast.error("Failed to reset password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    setIsLoading(true);
    setCodeError(null);

    try {
      // Use BetterAuth emailOtp to resend forget-password OTP
      const { error } = await emailOtp.sendVerificationOtp({
        email,
        type: "forget-password",
      });

      if (error) {
        toast.error(
          error.message || "Failed to resend code. Please try again.",
        );
      } else {
        toast.success("Reset code resent to your email");
        setResendCooldown(30);
      }
    } catch (err: any) {
      console.error("Resend code error:", err);
      toast.error("Failed to resend code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep("email");
    setEmail("");
    setCode("");
    setEmailNotFound(false);
    setResendCooldown(0);
    setCodeError(null);
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError(null);
    setVerifiedOtp("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogDescription>
            {step === "email" && "Enter your email to receive a reset code"}
            {step === "code" && `Enter the code sent to ${email}`}
            {step === "new-password" && "Set your new password"}
          </DialogDescription>
        </DialogHeader>

        {step === "email" && (
          <form onSubmit={handleSendCode} className="space-y-4">
            <div className="space-y-2">
              <Input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailNotFound(false);
                }}
                disabled={isLoading}
                required
              />
              {emailNotFound && (
                <p className="text-sm text-destructive">
                  This email isn't in our system.
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={!email.trim() || isLoading}
                className="flex-1"
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send Reset Code
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {step === "code" && (
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="space-y-4">
              <div className="flex justify-center">
                <InputOTP
                  value={code}
                  onChange={setCode}
                  maxLength={6}
                  pattern={REGEXP_ONLY_DIGITS}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <div className="flex justify-between items-center">
                <div>
                  {codeError === "invalid" && (
                    <p className="text-sm text-destructive">
                      Wrong code entered.
                    </p>
                  )}
                  {codeError === "expired" && (
                    <p className="text-sm text-destructive">
                      Code expired. Request a new one.
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="link"
                  onClick={handleResendCode}
                  disabled={isLoading || resendCooldown > 0}
                  className="px-0"
                >
                  {resendCooldown > 0
                    ? `Resend (${resendCooldown}s)`
                    : "Resend code"}
                </Button>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={code.length !== 6 || isLoading}
                className="flex-1"
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Verify
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {step === "new-password" && (
          <form onSubmit={handleUpdatePassword} className="space-y-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (passwordError) setPasswordError(null);
                  }}
                  disabled={isLoading}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Must be at least 8 characters with uppercase, lowercase, and
                  numbers
                </p>
              </div>

              <div className="space-y-2">
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (passwordError) setPasswordError(null);
                  }}
                  disabled={isLoading}
                  required
                />
              </div>

              {passwordError && (
                <p className="text-sm text-destructive">{passwordError}</p>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={!newPassword || !confirmPassword || isLoading}
                className="flex-1"
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Update Password
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
